import React from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Stack,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../redux/store'
import { logout } from '../../redux/keycloak/keycloakSlice'
import { useLicense } from '../../hooks/useLicense'

export default function LicenceExpiree() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { license, active, validTo, plan } = useLicense()

  const isSuspended = license && !active
  const formattedDate = validTo
    ? new Date(validTo).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  const handleLogout = () => {
    dispatch(logout())
    window.location.reload()
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
      }}
    >
      {/* Card */}
      <Box
        sx={{
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e7ecf3',
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.08)',
          p: { xs: 4, md: 6 },
          maxWidth: 480,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: isSuspended ? '#fff7ed' : '#fef2f2',
            border: `2px solid ${isSuspended ? '#fed7aa' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlinedIcon
            sx={{
              fontSize: 36,
              color: isSuspended ? '#ea580c' : '#dc2626',
            }}
          />
        </Box>

        {/* Title */}
        <Box textAlign="center">
          <Typography
            variant="h5"
            fontWeight={700}
            color="#132743"
            gutterBottom
          >
            {isSuspended
              ? t('license.expired.suspendedTitle')
              : t('license.expired.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
            {isSuspended
              ? t('license.expired.suspendedMessage')
              : t('license.expired.message')}
          </Typography>
        </Box>

        {/* Details */}
        <Box
          sx={{
            width: '100%',
            bgcolor: '#f8fafc',
            borderRadius: '10px',
            border: '1px solid #e7ecf3',
            p: 2.5,
          }}
        >
          <Stack spacing={1.5}>
            {plan && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('license.expired.plan')}
                </Typography>
                <Chip
                  label={t(`license.plans.${plan}`, plan)}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    borderColor: '#244A9A',
                    color: '#244A9A',
                  }}
                />
              </Stack>
            )}
            {formattedDate && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('license.expired.expiredOn')}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={isSuspended ? '#ea580c' : '#dc2626'}
                >
                  {formattedDate}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider sx={{ width: '100%', borderColor: '#e7ecf3' }} />

        {/* Contact */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <EmailOutlinedIcon sx={{ fontSize: 18, color: '#7a8aa6' }} />
          <Typography variant="body2" color="text.secondary">
            {t('license.expired.contact')}{' '}
            <Box
              component="a"
              href="mailto:contact@incyia.com"
              sx={{ color: '#244A9A', fontWeight: 600, textDecoration: 'none' }}
            >
              contact@incyia.com
            </Box>
          </Typography>
        </Stack>

        {/* Actions */}
        <Button
          variant="outlined"
          startIcon={<LogoutRoundedIcon />}
          onClick={handleLogout}
          fullWidth
          sx={{
            borderColor: '#e7ecf3',
            color: '#64748b',
            borderRadius: '10px',
            py: 1.25,
            fontWeight: 600,
            '&:hover': {
              borderColor: '#dc2626',
              color: '#dc2626',
              bgcolor: '#fef2f2',
            },
          }}
        >
          {t('logout.button')}
        </Button>
      </Box>

      {/* Footer */}
      <Typography variant="caption" color="#94a3b8" sx={{ mt: 4 }}>
        © {new Date().getFullYear()} INCYIA · GRC Platform
      </Typography>
    </Box>
  )
}
